// ============= VALIDATION METADATA (Single Source of Truth) =============
// Canonical hostname / domain matcher shared by the troubleshoot fields
// (DNS Lookup, Ping, Traceroute). A hostname is one or more dot-separated
// labels; each label is made of letters, digits, hyphens and underscores and
// must start and end with a letter, digit or underscore (no leading/trailing
// hyphen). This intentionally accepts:
//   - single-label hostnames without a domain suffix  -> "OnePlus-Nord-CE5"
//   - underscores in labels (DHCP/NetBIOS client names) -> "Wiring_Closet_South"
//   - consecutive hyphens, incl. IDN "xn--" punycode    -> "3152--TEST5--C"
//   - numeric characters in the final label (custom/local TLDs) -> ".lan2"
export const HOSTNAME_REGEX = /^([a-zA-Z0-9_]([a-zA-Z0-9_-]*[a-zA-Z0-9_])?\.)*[a-zA-Z0-9_]([a-zA-Z0-9_-]*[a-zA-Z0-9_])?$/;

// Structured validation rules for programmatic access and test automation
export const VALIDATION_METADATA = {
  organization: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: true,
    description: "Organization selection (dropdown)",
    errorMessages: {
      required: "validationSelectOrganization"
    }
  },

  orgName: {
    minLength: 1,
    maxLength: 128,
    regex: /[a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+/,
    isMandatory: true,
    description: "Organization name (text input)",
    errorMessages: {
      empty: "validationOrgNameEmpty",
      maxLength: "validationOrgNameLength",
      pattern: "validationOrgNameInvalidChars"
    }
  },

  locationName: {
    minLength: 3,
    maxLength: 24,
    regex: /^(?=[a-zA-Z0-9!@#$%^&()_.*-\s]{3,24}$)[^\s]+(?: +[^\s]+)*$/,
    isMandatory: true,
    description: "Location name",
    errorMessages: {
      required: "locationNameValidationMessage",
      minLength: "locationNameValidationMessage",
      maxLength: "locationNameValidationMessage",
      invalid: "locationNameValidationMessage"
    }
  },

  numberOfLocation: {
    minLength: 1,
    maxLength: 2,
    min: 1,
    max: 50,
    regex: /^[0-9]+$/,
    isMandatory: true,
    description: "Number of locations",
    errorMessages: {
      pattern: "validationValidNumber",
      range: "validationLocationCount"
    }
  },

  adminPassword: {
    minLength: 8,
    maxLength: 20,
    regex: /^[a-zA-Z0-9!@#$%^&*()]+$/,
    isMandatory: true,
    description: "Device admin password (location); 8-20 chars, 1 upper, 1 lower, 1 digit; allowed: a-zA-Z0-9!@#$%^&*() only",
    requirements: {
      uppercase: true,
      lowercase: true,
      number: true,
      noLeadingTrailingSpaces: true
    },
    errorMessages: {
      empty: "locationPasswordValidationMessage",
      minLength: "locationPasswordValidationMessage",
      maxLength: "locationPasswordValidationMessage",
      noSpaces: "locationPasswordValidationMessage",
      uppercase: "locationPasswordValidationMessage",
      lowercase: "locationPasswordValidationMessage",
      number: "locationPasswordValidationMessage",
      specialChar: "locationPasswordValidationMessage",
      invalidChars: "locationPasswordValidationMessage"
    }
  },

  mhsAdminPassword: {
    minLength: 8,
    maxLength: 20,
    isMandatory: true,
    description: "MHS device admin password; 8-20 characters (length only, no character-composition rules)",
    errorMessages: {
      empty: "mhsDeviceAdminPasswordValidation",
      minLength: "mhsDeviceAdminPasswordValidation",
      maxLength: "mhsDeviceAdminPasswordValidation"
    }
  },

  isoCountry: {
    minLength: 2,
    maxLength: 2,
    regex: /^[A-Z]{2}$/,
    isMandatory: true,
    description: "ISO country code",
    errorMessages: {
      required: "validationSelectCountry",
      invalid: "validationSelectCountry"
    }
  },

  onlyAlphaAndSpecial: {
    minLength: null,
    maxLength: null,
    regex: /^[a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+$/,
    isMandatory: false,
    description: "Alphanumeric with special characters",
    errorMessages: {
      pattern: "validationOrgNameInvalidChars"
    }
  },

  addressStreet: {
    minLength: null,
    maxLength: 100,
    regex: null,
    isMandatory: false,
    description: "Street address line (any characters; max 100)",
    errorMessages: {
      maxLength: "streetMaxLength"
    }
  },

  addressCity: {
    minLength: null,
    maxLength: 50,
    regex: null,
    isMandatory: false,
    description: "City (any characters; max 50)",
    errorMessages: {
      maxLength: "cityMaxLength"
    }
  },

  addressState: {
    minLength: null,
    maxLength: 50,
    regex: null,
    isMandatory: false,
    description: "State/province (any characters; max 50)",
    errorMessages: {
      maxLength: "stateMaxLength"
    }
  },

  postCode: {
    minLength: 3,
    maxLength: 15,
    regex: /^[0-9a-zA-Z- ]+$/,
    isMandatory: false,
    description: "Postal/ZIP code",
    errorMessages: {
      length: "validationZipCodeLength",
      pattern: "validationInvalidZipCode"
    }
  },

  uploadImg: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Image file upload",
    allowedExtensions: ['png', 'jpg', 'jpeg'],
    errorMessages: {
      invalidType: "validationImageFileTypes"
    }
  },

  userName: {
    minLength: 1,
    maxLength: null,
    regex: null,
    isMandatory: true,
    description: "User name",
    errorMessages: {
      required: "validationUsernameRequired"
    }
  },

  busiPhnNo: {
    minLength: 9,
    maxLength: 15,
    regex: /^[0-9]+$/,
    isMandatory: false,
    description: "Business phone number",
    errorMessages: {
      pattern: "validationPhoneDigitsOnly",
      length: "validationPhoneLength"
    }
  },

  role: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: true,
    description: "User role selection",
    errorMessages: {
      required: "validationSelectRole"
    }
  },

  name: {
    minLength: null,
    maxLength: null,
    regex: /^[a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+$/,
    isMandatory: true,
    description: "General name field with alphanumeric and special characters",
    errorMessages: {
      pattern: "validationOrgNameInvalidChars"
    }
  },

  mpskSettingsPassword: {
    minLength: 8,
    maxLength: 64,
    regex: null,
    isMandatory: true,
    description: "MPSK password",
    errorMessages: {
      required: "validationMpskPasswordLength",
      length: "validationMpskPasswordLength"
    }
  },

  vlanID: {
    minLength: 1,
    maxLength: 4,
    min: 1,
    max: 4093,
    regex: /^[0-9]+$/,
    isMandatory: true,
    description: "VLAN ID",
    errorMessages: {
      required: "validationVlanIdRequired",
      pattern: "validationVlanIdRange",
      range: "validationVlanIdRange",
      reserved: "validationVlanIdReserved"
    }
  },

  maxClients: {
    minLength: 1,
    maxLength: 2,
    min: 1,
    max: 50,
    regex: /^[0-9]+$/,
    isMandatory: true,
    description: "Maximum clients",
    errorMessages: {
      pattern: "validationMaxClientsRange",
      range: "validationMaxClientsRange"
    }
  },

  macAddress: {
    minLength: 17,
    maxLength: 17,
    regex: /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/,
    isMandatory: true,
    description: "MAC address",
    errorMessages: {
      required: "validationMacAddressEmpty",
      pattern: "validationValidMacAddress",
      invalid: "validationValidMacAddress"
    }
  },

  deviceName: {
    minLength: 1,
    maxLength: 255,
    regex: /^[a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+$/,
    isMandatory: true,
    description: "Device name",
    errorMessages: {
      required: "validationDeviceNameEmpty",
      pattern: "validationOrgNameInvalidChars"
    }
  },

  groupName: {
    minLength: 3,
    maxLength: 24,
    regex: /^[a-zA-Z0-9]+$/,
    isMandatory: true,
    description: "Device group name",
    errorMessages: {
      required: "validationDeviceGroupNameRequired",
      pattern: "validationDeviceGroupNameFormat",
      minLength: "validationDeviceGroupNameMinLength",
      maxLength: "validationDeviceGroupNameMaxLength"
    }
  },

  groupDescription: {
    minLength: null,
    maxLength: 255,
    regex: /^[a-zA-Z0-9!"#$%&'()*+,\-.\\/:;<=>?@[\]^_`{|}~ ]*$/,
    isMandatory: false,
    description: "Device group description",
    errorMessages: {
      pattern: "validationDeviceGroupDescriptionInvalidChars",
      maxLength: "validationDeviceGroupDescriptionLength"
    }
  },

  networkName: {
    minLength: 1,
    maxLength: 64,
    regex: /^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/,
    isMandatory: true,
    description: "Network name",
    errorMessages: {
      required: "validationNetworkNameRequired",
      pattern: "validationNetworkNameFormat",
      maxLength: "validationNetworkNameLength"
    }
  },

  vlanName: {
    minLength: 1,
    maxLength: 32,
    regex: /^(?! )^[0-9a-zA-Z-_]+( [0-9a-zA-Z-_]+)*$/,
    isMandatory: true,
    description: "VLAN name",
    errorMessages: {
      required: "validationVlanNameRequired",
      maxLength: "validationVlanNameLength",
      pattern: "validationVlanNameFormat"
    }
  },

  'vlan-des': {
    minLength: null,
    maxLength: 255,
    regex: /^[a-zA-Z0-9][a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+/,
    isMandatory: false,
    description: "VLAN description",
    errorMessages: {
      pattern: "validationVlanDescriptionFormat",
      maxLength: "validationVlanDescriptionLength"
    }
  },

  ipAddress: {
    minLength: null,
    maxLength: null,
    regex: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    isMandatory: true,
    description: "IP address",
    errorMessages: {
      required: "validationIpAddressRequired",
      pattern: "validationValidIpAddress"
    }
  },

  subnetMask: {
    minLength: null,
    maxLength: null,
    regex: /^(((255\.){3}(255|254|252|248|240|224|192|128|0+))|((255\.){2}(255|254|252|248|240|224|192|128|0+)\.0)|((255\.)(255|254|252|248|240|224|192|128|0+)(\.0+){2})|((255|254|252|248|240|224|192|128|0+)(\.0+){3}))$/,
    isMandatory: true,
    description: "Subnet mask",
    errorMessages: {
      required: "validationSubnetMaskRequired",
      pattern: "validationValidSubnetMask"
    }
  },

  gatewayAddress: {
    minLength: null,
    maxLength: null,
    regex: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    isMandatory: true,
    description: "Gateway address",
    errorMessages: {
      required: "validationGatewayAddressRequired",
      pattern: "validationValidGatewayAddress"
    }
  },

  prIpv6Address: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: true,
    description: "IPv6 address",
    errorMessages: {
      required: "validationPrIpv6AddressRequired",
      pattern: "validationPrIpv6Address"
    }
  },

  prIpv6OptionalAddress: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "IPv6 address",
    errorMessages: {
      pattern: "validationPrIpv6Address"
    }
  },

  prIpv6GatewayAddress: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: true,
    description: "IPv6 gateway address",
    errorMessages: {
      required: "validationPrIpv6GatewayAddressRequired",
      pattern: "validationPrIpv6GatewayAddress"
    }
  },

  prIpv6OptionalGatewayAddress: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "IPv6 gateway address",
    errorMessages: {
      pattern: "validationPrIpv6GatewayAddress"
    }
  },

  prIpv6DnsAddress: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: true,
    description: "Primary IPv6 DNS address",
    errorMessages: {
      required: "validationPrIpv6Dns1Required",
      pattern: "validationPrIpv6Dns1"
    }
  },

  prIpv6SecondaryDnsAddress: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Secondary IPv6 DNS address",
    errorMessages: {
      pattern: "validationPrIpv6Dns2"
    }
  },

  prIpv6PrefixLength: {
    minLength: null,
    maxLength: null,
    regex: null,
    min: 1,
    max: 128,
    isMandatory: true,
    description: "IPv6 prefix length",
    errorMessages: {
      required: "validationPrIpv6PrefixLengthRequired",
      range: "validationPrIpv6PrefixLengthRange"
    }
  },

  prIpv6DelegatedPrefixLength: {
    minLength: null,
    maxLength: null,
    regex: null,
    min: 48,
    max: 64,
    isMandatory: true,
    description: "IPv6 delegated prefix length",
    errorMessages: {
      required: "validationPrIpv6DelegatedPrefixLengthRequired",
      range: "validationPrIpv6DelegatedPrefixLengthRange"
    }
  },

  prIpv6OptionalDelegatedPrefixLength: {
    minLength: null,
    maxLength: null,
    regex: null,
    min: 48,
    max: 64,
    isMandatory: false,
    description: "IPv6 delegated prefix length",
    errorMessages: {
      range: "validationPrIpv6DelegatedPrefixLengthRange"
    }
  },

  password: {
    minLength: 6,
    maxLength: 128,
    regex: /^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z])[a-zA-Z0-9!@#$%^&*()]{6,128}$/,
    isMandatory: true,
    description: "Password",
    errorMessages: {
      required: "validationPasswordBlank",
      pattern: "validationGeneralPasswordFormat"
      }
  },

  changePassword: {
    minLength: 15,
    maxLength: 64,
    regex: /^(?!\s)(?!.*\s$).{15,64}$/,
    isMandatory: true,
    description: "Change Password Policy",
    errorMessages: {
        required: "validationPasswordRequired",
        pattern: "validationPasswordLength",
        mismatch: "validationPasswordMismatch"
      }
  },
  
  ipOrHostname: {
    minLength: 1,
    maxLength: 253,
    regex: /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$|^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    isMandatory: true,
    description: "IP address or hostname",
    errorMessages: {
      required: "validationIpAddressRequired",
      pattern: "validationValidIpAddress"
    }
  },

  domainInput: {
    minLength: 1,
    maxLength: 253,
    // DNS Lookup accepts a hostname or FQDN, including single-label device names
    // (e.g. "OnePlus-Nord-CE5"), names with underscores (e.g. "Wiring_Closet_South")
    // and local domains with a numeric label such as "3152--TEST5--C.lan2".
    regex: HOSTNAME_REGEX,
    isMandatory: true,
    description: "Domain name / hostname for DNS lookup",
    errorMessages: {
      required: "validationValidUrl",
      pattern: "validationValidUrl"
    }
  },

  serialNumber: {
    minLength: 13,
    maxLength: 13,
    regex: /^[123456789ABCDEFGHJKLMNPRSTUVWXYabcdefghjklmnprstuvwxy][0123456789ABCDEFGHJKLMNPRSTUVWXYabcdefghjklmnprstuvwxy]{12}$/,
    isMandatory: true,
    description: "Serial number",
    errorMessages: {
      required: "validationSerialNumberBlank",
      pattern: "validationValidSerialNumber"
    }
  },

  text: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Generic text field",
    errorMessages: {}
  },

  number: {
    minLength: null,
    maxLength: null,
    regex: /^[0-9]+$/,
    isMandatory: false,
    description: "Generic number field",
    errorMessages: {
      pattern: "validationValidNumber"
    }
  },

  time: {
    minLength: null,
    maxLength: null,
    regex: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
    isMandatory: false,
    description: "Time picker field",
    errorMessages: {
      pattern: "validationValidNumber"
    }
  },

  date: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Date picker field",
    errorMessages: {}
  },

  timeZone: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: true,
    description: "Time zone selection",
    errorMessages: {
      required: "timeZoneRequired"
    }
  },

  deviceGroup: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: true,
    description: "Device group selection",
    errorMessages: {
      required: "validationSelectDeviceGroup"
    }
  },

  numberOfKeys: {
    minLength: 1,
    maxLength: null,
    min: 1,
    max: 1000,
    regex: /^[0-9]+$/,
    isMandatory: true,
    description: "Number of keys",
    errorMessages: {
      required: "validationValidNumber",
      pattern: "validationValidNumber",
      range: "validationValidNumber"
    }
  },

  phoneNo: {
    minLength: 10,
    maxLength: 10,
    regex: /^[0-9]{10}$/,
    isMandatory: false,
    description: "Phone number",
    errorMessages: {
      pattern: "validationPhoneNumber"
    }
  },

  alphaNum: {
    minLength: null,
    maxLength: null,
    regex: /^[a-zA-Z0-9!"#$%&'()*+,-.\\\/:;<=>?@[\]^_`{|}~ ]+$/,
    isMandatory: false,
    description: "Alphanumeric field",
    errorMessages: {
      pattern: "validationOrgNameInvalidChars"
    }
  },

  lastName: {
    minLength: null,
    maxLength: null,
    regex: /^[a-zA-Z0-9!"#$%&'()*+,-.\\\/:;<=>?@[\]^_`{|}~ ]+$/,
    isMandatory: false,
    description: "Last name field",
    errorMessages: {
      pattern: "validationOrgNameInvalidChars"
    }
  },

  imeiData: {
    minLength: 15,
    maxLength: 15,
    regex: /^[a-zA-Z0-9]{15}$/,
    isMandatory: true,
    description: "IMEI number",
    errorMessages: {
      required: "validationInvalidIMEI",
      pattern: "validationInvalidIMEI"
    }
  },

  deviceNumber: {
    minLength: 6,
    maxLength: 20,
    regex: /^[0-9]{6,20}$/,
    isMandatory: false,
    description: "Device number",
    errorMessages: {
      pattern: "validationDeviceNumberFormat"
    }
  },

  ssidName: {
    minLength: 1,
    maxLength: 32,
    regex: /^[^\/"\s]+(\s+[^\/"\s]+)*$/,
    isMandatory: true,
    description: "SSID name",
    errorMessages: {
      required: "validationSsidNameRequired",
      maxLength: "validationSsidNameLength",
      pattern: "validationSsidNameInvalidChars"
    }
  },

  ssidPassword: {
    minLength: 8,
    maxLength: 63,
    regex: null,
    isMandatory: true,
    description: "SSID password",
    errorMessages: {
      required: "validationSsidPasswordLength",
      length: "validationSsidPasswordLength"
    }
  },

  wifiPassword: {
    minLength: 8,
    maxLength: 63,
    regex: null,
    isMandatory: false,
    description: "WiFi password",
    errorMessages: {
      length: "validationWifiPasswordLength"
    }
  },

  ip: {
    minLength: null,
    maxLength: null,
    regex: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    isMandatory: true,
    description: "IP address",
    errorMessages: {
      required: "validationIpAddressRequired",
      pattern: "validationValidIpAddress"
    }
  },

  subMask: {
    minLength: null,
    maxLength: null,
    regex: /^(((255\.){3}(255|254|252|248|240|224|192|128|0+))|((255\.){2}(255|254|252|248|240|224|192|128|0+)\.0)|((255\.)(255|254|252|248|240|224|192|128|0+)(\.0+){2})|((255|254|252|248|240|224|192|128|0+)(\.0+){3}))$/,
    isMandatory: true,
    description: "Subnet mask",
    errorMessages: {
      required: "validationSubnetMaskRequired",
      pattern: "validationValidSubnetMask"
    }
  },

  cpURL: {
    minLength: 1,
    maxLength: null,
    regex: /[a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+/,
    isMandatory: true,
    description: "Captive portal URL",
    errorMessages: {
      required: "validationCaptivePortalUrlRequired",
      pattern: "validationValidUrl"
    }
  },

  nasIdentifier: {
    minLength: 1,
    maxLength: 64,
    regex: null,
    isMandatory: true,
    description: "NAS Identifier",
    errorMessages: {
      required: "validationNasIdentifierRequired",
      maxLength: "validationNasIdentifierLength"
    }
  },

  radusPort: {
    minLength: null,
    maxLength: null,
    min: 1,
    max: 65535,
    regex: /^[0-9]+$/,
    isMandatory: true,
    description: "RADIUS port",
    errorMessages: {
      required: "validationRadiusPortRequired",
      pattern: "validationRadiusPortRange",
      range: "validationRadiusPortRange"
    }
  },

  radusSecretKey: {
    minLength: 6,
    maxLength: 128,
    regex: null,
    isMandatory: true,
    description: "RADIUS secret key",
    errorMessages: {
      required: "validationRadiusSecretKeyRequired",
      length: "validationRadiusSecretKeyLength"
    }
  },

  secIPAddress: {
    minLength: null,
    maxLength: null,
    regex: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    isMandatory: false,
    description: "Secondary IP address",
    errorMessages: {
      pattern: "validationValidSecondaryIpAddress"
    }
  },

  lagName: {
    minLength: 1,
    maxLength: 32,
    regex: /^[a-zA-Z0-9]{1}[a-zA-Z0-9_-]*$/,
    isMandatory: true,
    description: "LAG name",
    errorMessages: {
      required: "validationLagNameRequired",
      pattern: "validationLagNameFormat",
      maxLength: "validationLagNameLength"
    }
  },

  snmpIpAddress: {
    minLength: null,
    maxLength: null,
    regex: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    isMandatory: false,
    description: "SNMP IP address",
    errorMessages: {
      pattern: "validationValidSnmpIpAddress"
    }
  },

  snmpCommunityString: {
    minLength: 1,
    maxLength: 64,
    regex: null,
    isMandatory: true,
    description: "SNMP community string",
    errorMessages: {
      required: "validationSnmpCommunityStringRequired",
      maxLength: "validationSnmpCommunityStringLength"
    }
  },

  syslogServerIp: {
    minLength: null,
    maxLength: null,
    regex: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    isMandatory: false,
    description: "Syslog server IP address",
    errorMessages: {
      pattern: "validationValidSyslogServerIp"
    }
  },

  sysLogPort: {
    minLength: null,
    maxLength: null,
    min: 1,
    max: 65535,
    regex: /^[0-9]+$/,
    isMandatory: false,
    description: "Syslog port",
    errorMessages: {
      pattern: "validationSyslogPortRange",
      range: "validationSyslogPortRange"
    }
  },

  customGroupName: {
    minLength: 1,
    maxLength: 64,
    regex: /^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/,
    isMandatory: true,
    description: "Custom group name",
    errorMessages: {
      required: "validationCustomGroupNameRequired",
      pattern: "validationCustomGroupNameFormat",
      maxLength: "validationCustomGroupNameLength"
    }
  },

  customGroupDescription: {
    minLength: 1,
    maxLength: 512,
    regex: null,
    isMandatory: true,
    description: "Custom group description",
    errorMessages: {
      required: "validationCustomGroupDescriptionRequired",
      maxLength: "validationCustomGroupDescriptionLength"
    }
  },

  orbiSsid: {
    minLength: 1,
    maxLength: 32,
    regex: /^[^\/"\s]+(\s+[^\/"\s]+)*$/,
    isMandatory: true,
    description: "ORBI SSID name",
    errorMessages: {
      required: "validationOrbiSsidRequired",
      maxLength: "validationOrbiSsidFormat",
      pattern: "validationOrbiSsidFormat"
    }
  },

  ssidOrbiPassword: {
    minLength: 8,
    maxLength: 63,
    regex: /^\S*$/,
    isMandatory: true,
    description: "ORBI SSID password",
    errorMessages: {
      required: "validationOrbiSsidPasswordFormat",
      length: "validationOrbiSsidPasswordFormat",
      pattern: "validationOrbiSsidPasswordFormat"
    }
  },

  // 64, not 255: matches the standalone enforcement (legacy data-type="port-config-des") and the
  // maxLength both Port Configuration screens already ship.
  'port-config-des': {
    minLength: null,
    maxLength: 64,
    // Anchored: unanchored, "abc中文" passed because a valid substring matched. This entry is what
    // nonregexTesting actually applies — its metadata path runs before the switch, so the duplicate
    // 'port-config-des' cases in UiValidate are unreachable for any registered type.
    regex: /^[a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+$/,
    isMandatory: false,
    description: "Port configuration description",
    errorMessages: {
      pattern: "validationPortDescriptionInvalidChars",
      maxLength: "validationPortDescriptionLength"
    }
  },

  radusPortMhs: {
    minLength: null,
    maxLength: null,
    min: 1,
    max: 65535,
    regex: /^[0-9]+$/,
    isMandatory: true,
    description: "RADIUS port (MHS devices)",
    errorMessages: {
      required: "validationRadiusPortRequired",
      pattern: "validationRadiusPortRange",
      range: "validationRadiusPortRange"
    }
  },

  radusPortOrbi: {
    minLength: null,
    maxLength: null,
    min: 1,
    max: 65535,
    regex: /^[0-9]+$/,
    isMandatory: true,
    description: "RADIUS port (ORBI devices)",
    errorMessages: {
      required: "validationRadiusPortRequired",
      pattern: "validationRadiusPortRange",
      range: "validationRadiusPortRange"
    }
  },

  email: {
    minLength: null,
    maxLength: 254,
    localPartMaxLength: 64,
    regex: /^(([^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+(\.[^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    isMandatory: true,
    description: "Email address (required)",
    errorMessages: {
      required: "validationEmailRequired",
      pattern: "validEmailAddress",
      maxLength: "validationEmailMaxLength",
      localPartMaxLength: "validationEmailLocalPartMaxLength"
    }
  },

  shareEmail: {
    minLength: null,
    maxLength: 254,
    localPartMaxLength: 64,
    regex: /^(([^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+(\.[^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    isMandatory: false,
    description: "Share email (optional)",
    errorMessages: {
      pattern: "validEmailAddress",
      maxLength: "validationEmailMaxLength",
      localPartMaxLength: "validationEmailLocalPartMaxLength"
    }
  },

  orgEmail: {
    minLength: null,
    maxLength: 254,
    localPartMaxLength: 64,
    regex: /^(([^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+(\.[^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    isMandatory: false,
    description: "Organization email (optional)",
    errorMessages: {
      pattern: "validEmailAddress",
      maxLength: "validationEmailMaxLength",
      localPartMaxLength: "validationEmailLocalPartMaxLength"
    }
  },

  uploadImg2: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Image file upload (alternative)",
    allowedExtensions: ['png', 'jpg', 'jpeg', 'gif'],
    errorMessages: {
      invalidType: "validationImageFormatsSupported"
    }
  },

  uploadBackup: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: true,
    description: "Backup file upload",
    allowedExtensions: ['zip', 'rar'],
    errorMessages: {
      invalidType: "validationBackupFormatsSupported"
    }
  },

  validateRuleName: {
    minLength: 1,
    maxLength: 32,
    regex: /^[a-zA-Z0-9 _-]{1,32}$/,
    isMandatory: true,
    description: "Rule name",
    errorMessages: {
      required: "validationRuleNameFormat",
      pattern: "validationRuleNameFormat"
    }
  },

  subnetNetworkMask: {
    minLength: null,
    maxLength: null,
    min: 8,
    max: 32,
    regex: /^[0-9]+$/,
    isMandatory: true,
    description: "Subnet network mask (CIDR notation)",
    errorMessages: {
      required: "validationNetworkMaskRange",
      pattern: "validationNetworkMaskRange",
      range: "validationNetworkMaskRange"
    }
  },

  urlFilteringDomainValidate: {
    minLength: 1,
    maxLength: 253,
    regex: /^(http[s]?:\/\/){0,1}(www\.){0,1}([a-z0-9A-Z]+(-[a-z0-9A-Z]+)*\.)+[a-zA-Z]{2,}$/,
    isMandatory: true,
    description: "URL filtering domain",
    errorMessages: {
      required: "validationValidUrl",
      pattern: "validationValidUrl"
    }
  },

  snmpServerValidations: {
    minLength: 1,
    maxLength: 64,
    regex: /^[^\s]{1,64}$/,
    isMandatory: true,
    description: "SNMP Server parameters (System Name, System Location, etc.)",
    errorMessages: {
      required: "validationValidNumber",
      pattern: "validationValidNumber",
      maxLength: "validationValidNumber"
    }
  },

  snmpPasswordValidation: {
    minLength: 8,
    maxLength: 64,
    regex: null,
    isMandatory: true,
    description: "SNMP v3 password",
    errorMessages: {
      required: "snmpAuthPasswordValidationError",
      length: "snmpAuthPasswordValidationError"
    }
  },
  snmpAuthPasswordValidation: {
    minLength: 8,
    maxLength: 64,
    regex: null,
    isMandatory: true,
    description: "SNMP v3 authentication password",
    errorMessages: {
      required: "snmpAuthPasswordValidationError",
      length: "snmpAuthPasswordValidationError"
    }
  },
  snmpEncryptionPasswordValidation: {
    minLength: 8,
    maxLength: 64,
    regex: null,
    isMandatory: true,
    description: "SNMP v3 encryption password",
    errorMessages: {
      required: "snmpEncryptionPasswordValidationError",
      length: "snmpEncryptionPasswordValidationError"
    }
  },

  snmpSystemContactValidation: {
    minLength: 1,
    maxLength: 64,
    regex: /^(?!.*\s)([^\s@]{1,64}@[A-Za-z0-9.-]+\.[A-Za-z]{2,64}|[^\s]{1,64})$/,
    isMandatory: true,
    description: "SNMP System Contact",
    errorMessages: {
      required: "validationValidNumber",
      pattern: "validationValidNumber",
      maxLength: "validationValidNumber"
    }
  },

  snmpPort: {
    minLength: null,
    maxLength: null,
    min: 1,
    max: 65535,
    regex: /^[0-9]+$/,
    isMandatory: false,
    description: "SNMP Port",
    errorMessages: {
      pattern: "validationSyslogPortRange",
      range: "validationSyslogPortRange"
    }
  },

  snmpUserName: {
    minLength: 1,
    maxLength: 64,
    regex: /^[^\s]+$/,
    isMandatory: true,
    description: "SNMP v3 User Name",
    errorMessages: {
      required: "snmpUserNameValidationError",
      maxLength: "snmpUserNameValidationError",
      pattern: "snmpUserNameValidationError"
    }
  },

  snmpCommunityName: {
    minLength: 1,
    maxLength: 64,
    regex: null,
    isMandatory: true,
    description: "SNMP Community Name",
    errorMessages: {
      required: "validationSnmpCommunityStringRequired",
      maxLength: "snmpCommunityNameValidationError",
      minLength: "snmpCommunityNameValidationError"
    }
  },

  snmpClientIpAddress: {
    minLength: null,
    maxLength: null,
    regex: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    isMandatory: false,
    description: "SNMP Client IP Address",
    errorMessages: {
      pattern: "validationValidIpAddress"
    }
  },

  bkupName: {
    minLength: 1,
    maxLength: 32,
    regex: /^[a-zA-Z0-9.,\\ -]+$/,
    isMandatory: true,
    description: "Backup name",
    errorMessages: {
      required: "validationValidNumber",
      maxLength: "validationValidNumber",
      pattern: "validationValidNumber"
    }
  },

  bkupDesc: {
    minLength: 1,
    maxLength: 32,
    regex: /^[a-zA-Z0-9.,\\ -]+$/,
    isMandatory: true,
    description: "Backup description",
    errorMessages: {
      required: "validationValidNumber",
      maxLength: "validationValidNumber",
      pattern: "validationValidNumber"
    }
  },

  ipMask: {
    minLength: null,
    maxLength: null,
    regex: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    isMandatory: true,
    description: "IP Mask (subnet mask)",
    errorMessages: {
      required: "validationSubnetMaskRequired",
      pattern: "validationValidSubnetMask"
    }
  },

  // UI Element Types (for elements that don't have specific validation rules)
  checkbox: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Checkbox field",
    errorMessages: {}
  },

  radioButton: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Radio button field",
    errorMessages: {}
  },

  switch: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Switch/toggle field",
    errorMessages: {}
  },

  Switch: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Switch/toggle field (alias)",
    errorMessages: {}
  },

  slider: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Slider field",
    errorMessages: {}
  },

  select: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Select/dropdown field",
    errorMessages: {}
  },

  file: {
  minLength: null,
  maxLength: null,
  regex: null,
  isMandatory: true,
  description: "Device List file",
  allowedExtensions: ['csv'],
  errorMessages: {
    invalidType: "validationCSVFormatSupported"
  }
},


  textField: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Generic text field (alias for text)",
    errorMessages: {}
  },

  accordion: {
    minLength: null,
    maxLength: null,
    regex: null,
    isMandatory: false,
    description: "Accordion toggle element (clickable expand/collapse)",
    errorMessages: {}
  },
  
  // Network Testing / Diagnostics
  initTtl: {
    minLength: null,
    maxLength: null,
    min: 1,
    max: 255,
    regex: /^[0-9]+$/,
    isMandatory: false,
    description: "Initial TTL for traceroute",
    errorMessages: {
      pattern: "validationValidNumber",
      range: "validationTtlRange"
    }
  },

  maxTtl: {
    minLength: null,
    maxLength: null,
    min: 1,
    max: 255,
    regex: /^[0-9]+$/,
    isMandatory: false,
    description: "Maximum TTL for traceroute",
    errorMessages: {
      pattern: "validationValidNumber",
      range: "validationTtlRange"
    }
  },

  packetSize: {
    minLength: null,
    maxLength: null,
    min: 1,
    max: 65507,
    regex: /^[0-9]+$/,
    isMandatory: false,
    description: "Packet size for network testing",
    errorMessages: {
      pattern: "validationValidNumber",
      range: "validationPacketSizeRange"
    }
  },

  packetsPerHop: {
    minLength: null,
    maxLength: null,
    min: 1,
    max: 100,
    regex: /^[0-9]+$/,
    isMandatory: false,
    description: "Packets per hop for traceroute",
    errorMessages: {
      pattern: "validationValidNumber",
      range: "validationPacketsPerHopRange"
    }
  },

  interval: {
    minLength: null,
    maxLength: null,
    min: 0,
    max: 3600,
    regex: /^[0-9]+$/,
    isMandatory: false,
    description: "Interval in seconds",
    errorMessages: {
      pattern: "validationValidNumber",
      range: "validationIntervalRange"
    }
  },

  port: {
    minLength: null,
    maxLength: null,
    min: 1,
    max: 65535,
    regex: /^[0-9]+$/,
    isMandatory: false,
    description: "Port number",
    errorMessages: {
      pattern: "validationValidNumber",
      range: "validationPortRange"
    }
  },

  // Additional Network Fields
  mac: {
    minLength: 17,
    maxLength: 17,
    regex: /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/,
    isMandatory: false,
    description: "MAC address (alias)",
    errorMessages: {
      required: "validationMacAddressEmpty",
      pattern: "validationValidMacAddress",
      invalid: "validationValidMacAddress"
    }
  },

  mobileProfileName: {
    minLength: 1,
    maxLength: 50,
    regex: /^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/,
    isMandatory: true,
    description: "Mobile profile name",
    errorMessages: {
      required: "validationProfileNameRequired",
      pattern: "validationProfileNameFormat",
      maxLength: "validationProfileNameLength"
    }
  },

  // Mobile Router Settings Validations
  dnsAddress: {
    minLength: null,
    maxLength: null,
    regex: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    isMandatory: false,
    description: "DNS Server IP Address (optional)",
    errorMessages: {
      pattern: "invalidIPAddress"
    }
  },

  secondaryDNSAddress: {
    minLength: null,
    maxLength: null,
    regex: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    isMandatory: false,
    description: "Secondary DNS Server IP Address (optional)",
    errorMessages: {
      pattern: "invalidIPAddress"
    }
  },

  // Dynamic DNS (DDNS) Validations
  ddnsProfileName: {
    minLength: 1,
    maxLength: 64,
    regex: /^[a-zA-Z0-9!"#$%&'()*+,-.\\\/:;<=>?@[\]^_`{|}~ ]+$/,
    isMandatory: true,
    description: "DDNS profile/policy name",
    errorMessages: {
      required: "ddns-profile-name-length-error",
      minLength: "ddns-profile-name-length-error",
      maxLength: "ddns-profile-name-length-error",
      length: "Valid Profile Name should be 1-64 characters in length."
    }
  },

  ddnsHostname: {
    minLength: 1,
    maxLength: 253,
    regex: /(?:[a-z0-9](?:[a-z0-9-@]{0,61}[a-z0-9-@])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/i,
    isMandatory: true,
    description: "DDNS hostname/domain name (supports @ character as per PRJCBUGEN-49544)",
    normalizeFunction: (value) => value.replace(/@/g, '.'),
    errorMessages: {
      required: "ddns-hostname-required",
      empty: "Domain name is required",
      pattern: "ddns-hostname-invalid",
      invalid: "Please enter a valid domain name (e.g., example.com)"
    }
  },

  ddnsPassword: {
    minLength: 1,
    maxLength: 256,
    regex: null,
    isMandatory: true,
    description: "DDNS password",
    errorMessages: {
      required: "ddns-password-range",
      length: "ddns-password-range",
      range: "Password must be 1-256 characters"
    }
  }
};
